import { useRBAC, UserCan } from "@/hooks/useRBAC"
import { useLoginUser, useSwitchCustomer } from "@/hooks/useSession"
import { Session } from "@/types/Session"
import { LoginCustomer } from "@/types/__gql__"
import React, { createContext, useEffect, useState } from "react"

import Loading from "@/components/Loading"

export const UserContext = createContext<{
  session: Session
  userCan: UserCan
} | null>(null)

export const SessionContextProvider = ({
  customerId,
  children
}: {
  customerId?: string
  children: React.ReactNode
}) => {
  const [currentCustomer, setCurrentCustomer] = useState<Partial<LoginCustomer>>()
  const { data: loginUser, loading } = useLoginUser()
  const { getCustomer, data: switchedCustomer } = useSwitchCustomer()
  const { userCan } = useRBAC(loginUser?.roles)

  useEffect(() => {
    if (loginUser) {
      const { id, roles, email } = loginUser

      // use the Hotjar function to set the user attribute
      ;(window as any).hj &&
        (window as any).hj("identify", id, {
          email,
          role: roles.join("/")
        })

      // If login user's customer id is the same as current customer, do not need to call getCustomer
      if (loginUser?.customer?.id !== customerId) {
        getCustomer({ variables: { customerId: customerId } })
      } else {
        setCurrentCustomer({
          id: loginUser?.customer?.id,
          customerName: loginUser?.customer?.customerName
        })
      }
    }
  }, [customerId, getCustomer, loginUser])

  useEffect(() => {
    if (switchedCustomer) {
      setCurrentCustomer({
        id: switchedCustomer._id,
        customerName: switchedCustomer.customerName
      })
    }
  }, [switchedCustomer])

  useEffect(() => {
    if (loginUser?.customer?.id && !customerId) {
      window.location.replace(
        `//${window.location.host}/customers/${loginUser?.customer?.id}/vacancies`
      )
    }
  }, [customerId, loginUser])

  return loading || !currentCustomer ? (
    <Loading />
  ) : (
    <UserContext.Provider
      value={{
        userCan,
        session: {
          user: loginUser,
          currentCustomer: currentCustomer
        }
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
